import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * `staff_documents` does NOT exist in any migration, and there's no
 * `staff-documents` storage bucket either. Per the vault
 * (06-Specs/User Stories — Admin.md §HR): "HR module MVP scope pending —
 * Open Items." This isn't a naming bug to quietly patch — it's schema that
 * hasn't been decided yet (never-invent-policy.md), so outside demo mode
 * these hooks fail loudly instead of querying a table that was never built.
 * Once HR module scope is confirmed, this needs: a migration for
 * staff_documents (+ RLS: HR/admin/superadmin read, staff read-own) and a
 * private Storage bucket with signed-URL access, then these can call the
 * real thing.
 */
const NOT_IMPLEMENTED = new Error(
  "Staff documents aren't in the schema yet (Open Item — see vault User Stories §HR). Ask Leon before building against an assumed table shape.",
);

/**
 * The shape DocumentsPage renders. Hand-declared ON PURPOSE (exception to
 * "never hand-write a row type"): there is no staff_documents table to
 * generate from yet — this is the UI contract the future migration must
 * satisfy, and it keeps the always-throwing query fns from inferring
 * `Promise<never>` (which breaks typecheck at every `.filter`/`.map` caller).
 */
export interface StaffDocument {
  id: string;
  staffName: string;
  type: string;
  name: string;
  expiryDate: string | null;
  status: string;
  file_path?: string;
}

export function useStaffDocuments(staffId: string | null) {
  return useQuery({
    queryKey: ["staff-documents", staffId],
    queryFn: async (): Promise<StaffDocument[]> => {
      throw NOT_IMPLEMENTED;
    },
    enabled: !!staffId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useAllDocuments() {
  return useQuery({
    queryKey: ["all-documents"],
    queryFn: async (): Promise<StaffDocument[]> => {
      throw NOT_IMPLEMENTED;
    },
    staleTime: 2 * 60_000,
    retry: false,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      staffId: string;
      file: File;
      type: string;
      expiryDate: string | null;
    }): Promise<{ success: boolean }> => {
      void input; // not implemented — see NOT_IMPLEMENTED above
      throw NOT_IMPLEMENTED;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-documents"] });
      queryClient.invalidateQueries({ queryKey: ["staff-documents"] });
    },
  });
}

export function useGetDocumentUrl() {
  return useMutation({
    mutationFn: async (filePath: string): Promise<{ url: string }> => {
      void filePath; // not implemented — see NOT_IMPLEMENTED above
      throw NOT_IMPLEMENTED;
    },
  });
}
